import _                                      from 'underscore';
import React, { useEffect, useRef, useState } from 'react';
import { Form }                               from 'semantic-ui-react';
import { Formik, FormikConfig, FormikProps }  from 'formik';
import * as Yup                               from 'yup';
import { useQuery }                           from '@apollo/react-hooks';

import { findTopSimilarityString, runIfFunction }                         from '@src/util';
import { Align, CorvalStaticProjectActivity, Size, Uuid }                 from '@src/types';
import { queriesHaveData, queriesLoading }                                from '@src/store/_apolloUtil';
import { BasicDialogContainer }                                           from '../../../components/modals/BasicDialogContainer';
import { StyledContainer }                                                from '../../../containers';
import { LoadingIndicator }                                               from '../../../components/util/loading/LoadingIndicator';
import { ActivityQueries }                                                from "@src/queries/ActivityQueries";
import { BasicDialog, BasicDialogProps }                                  from "@src/views/components/modals/BasicDialog";
import { addStringMinCharDescriptorTest, useCategories, useFeatureFlags } from "@src/hooks";
import { TextAreaField, TextField }                                       from "@src/views/components/forms";
import { WarningMessage }                                                 from "@src/views/components/util/messaging/WarningMessage";
import { SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD }                     from "@src/constants";
import { QueryOpDialog }                                                  from "@src/views/components/from-query/QueryOpDialog";
import { ProjectQueries }                                                 from "@src/queries/ProjectQueries";
import { ProjectCategoryDropdown }                                        from "@src/views/components/forms/dropdowns/ProjectCategoryDropdown";


export interface UpdateProjectTaxonomyDialogOnCompleteReturnData {
    categoryId: Uuid,
    subCategoryId: Uuid,
    values: {
        name: string,
        description: string,
        categoryId: string,
    },
}

interface FormValues {
    name: string,
    description: string,
    categoryId: string,
}

type FormConfig = FormikConfig<FormValues>;

export const getInitialValues = ( values: Partial<FormValues> = {} ): FormValues => ( {
    name:        '',
    description: '',
    categoryId:  '',
    ...values
} );

interface UpdateProjectTaxonomyDialogProps extends BasicDialogProps {

    // The project this taxonomy belongs to
    projectId: Uuid,

    // The project version this taxonomy belongs to
    projectVersionId: string,

    // the category ID to update details on
    categoryId?: Uuid,

    // the subCategory ID to update details on
    subCategoryId?: Uuid,

    // if no supporting activity can be updated, fill initial values from
    // external source (eg: a Gantt task store)
    initialValues?: Partial<FormValues>,

    // A number describing how close the naming can be before the dialog
    // throws a warning about similar naming existing in the project
    maxNamingScoreThreshold?: number,

    // Whether to update via modal dialog, or pass through results
    noOp?: boolean,

    // What to run after completion
    onComplete?( data: UpdateProjectTaxonomyDialogOnCompleteReturnData ): void,
}

const UpdateProjectTaxonomyDialogDefaultProps = {
    projectId:               null,
    projectVersionId:        null,
    categoryId:              null,
    subCategoryId:           null,
    initialValues:           {},
    maxNamingScoreThreshold: SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD,
    noOp:                    false,
} satisfies Partial<UpdateProjectTaxonomyDialogProps>

function UpdateProjectTaxonomyDialog( __props: UpdateProjectTaxonomyDialogProps ){

    const _props = { ...UpdateProjectTaxonomyDialogDefaultProps, ...__props };

    const { projectId, projectVersionId, initialValues, ...props } = _props,
          [ existingTaxonomy, setExistingTaxonomy ]                = useState<{ categoryId: Uuid, name: string, description: string, }>( { categoryId: '', name: '', description: '', } ),
          [ existingTaxonomyNames, setExistingTaxonomyNames ]      = useState( [] ),
          [ maxScoreThreshold ]                                    = useState( props.maxNamingScoreThreshold ),
          { findAllCategoryNames, findAllSubCategoryNames }        = useCategories(),
          featureFlags                                             = useFeatureFlags(),
          formikRef                                                = useRef<FormikProps<FormValues>>( null );

    const isSubCategory = !_.isEmpty( props.subCategoryId );

    // Get all static project activities for the given project version and the taxonomy info therein
    const getStaticProjectActivitiesReq = useQuery<{ staticProjectActivities: CorvalStaticProjectActivity[] }>(
        ActivityQueries.queries.GET_PROJECT_ACTIVITIES( `
            id 
            categoryId 
            categoryName 
            categoryDescription 
            subCategoryId 
            subCategoryName 
            subCategoryDescription
        `, projectVersionId ), {

            variables:   { projectVersionId },
            skip:        ( !projectId || !projectVersionId ),
            fetchPolicy: 'network-only'
        } );

    const isLoading = queriesLoading( [ getStaticProjectActivitiesReq ] );

    const getPrefixKey = () => {

        return isSubCategory ? 'subCategory' : 'category';
    }

    const getAllTaxonomyNamesForComparison = async () => {

        return isSubCategory
               ? await findAllSubCategoryNames( projectId, projectVersionId )
               : await findAllCategoryNames( projectId, projectVersionId );
    }

    const getRelevantProjectCategory = ( categoryId: Uuid ) => {

        const activities = getStaticProjectActivitiesReq.data.staticProjectActivities;

        return activities.find( a => a.categoryId === categoryId );
    }

    const getAllRelevantProjectCategories = () => {

        const activities = getStaticProjectActivitiesReq.data.staticProjectActivities;

        return _.pluck( activities, 'categoryId' );
    }

    const getAllRelevantProjectActivities = () => {

        const activities = getStaticProjectActivitiesReq.data.staticProjectActivities;

        return activities
            .filter( activities => {
                return isSubCategory
                       ? ( activities.subCategoryId === props.subCategoryId )
                       : ( activities.categoryId === props.categoryId )
            } );
    }

    useEffect( () => {

        getAllTaxonomyNamesForComparison().then( existingNames => {

            setExistingTaxonomyNames( existingNames );
        } )

    }, [] );

    useEffect( () => {

        if( queriesHaveData( [ getStaticProjectActivitiesReq ] ) ){

            const updatableActivities = getAllRelevantProjectActivities(),
                  emptyValues         = initialValues as UpdateProjectTaxonomyDialogProps['initialValues'],
                  delegateActivity    = _.first( updatableActivities ) ?? {
                      [ `${ getPrefixKey() }Name` ]:        emptyValues?.name ?? '',
                      [ `${ getPrefixKey() }Description` ]: emptyValues?.description ?? '',
                      [ `categoryId` ]:                     emptyValues?.categoryId ?? '',
                  };

            setExistingTaxonomy( {
                name:        delegateActivity[ `${ getPrefixKey() }Name` ],
                description: delegateActivity[ `${ getPrefixKey() }Description` ],
                categoryId:  delegateActivity[ `categoryId` ],
            } );
        }

    }, [ getStaticProjectActivitiesReq ] );

    /* FORM SETUP
     ====================================== */
    addStringMinCharDescriptorTest( 'hasDescriptorMinChar', {
        minChar: featureFlags.systemSettingFormFieldsDescriptorsMinCharacterLength
    } );

    let formProps: FormConfig = {

        innerRef:           formikRef,
        enableReinitialize: true,
        isInitialValid:     false,
        initialValues:      getInitialValues( {
            name:        existingTaxonomy.name,
            description: existingTaxonomy.description,
            categoryId:  existingTaxonomy.categoryId,
        } ),
        validateOnChange:   true,

        validationSchema: Yup.object( {
            name:        Yup.string().min( 3 ).required( 'Name is required' ),
            description: Yup.string().hasDescriptorMinChar(),

        } ),

        onSubmit: null,
    };

    const getGraphArgs = () => {

        const { values } = formikRef.current;

        let taxonomyChangeData = {
            [ `${ getPrefixKey() }Id` ]:          props[ `${ getPrefixKey() }Id` ],
            [ `${ getPrefixKey() }Name` ]:        values.name,
            [ `${ getPrefixKey() }Description` ]: values.description,
        };

        // Handle category migration
        if( isSubCategory && values.categoryId !== props.subCategoryId ){

            const categoryActivityDelegate = getRelevantProjectCategory( values.categoryId )

            taxonomyChangeData = {
                ...taxonomyChangeData,
                'categoryId':          values.categoryId,
                'categoryName':        categoryActivityDelegate.categoryName,
                'categoryDescription': categoryActivityDelegate.categoryDescription,
            }
        }

        // All activity change data to be passed
        const activitiesToModify = getAllRelevantProjectActivities().map( activity => ( {
            id: activity.id,
            ...taxonomyChangeData
        } ) )

        return {
            variables: {
                input: {
                    projectVersionId: projectVersionId,
                    activityChanges:  {
                        modified: activitiesToModify
                    }
                }
            }
        };
    }

    const handleOnSubmit = async () => {

        const { values } = formikRef.current;

        return Promise.resolve( values );
    }

    const handleOnComplete = async ( data: any ) => {

        const { values } = formikRef.current;

        runIfFunction( props.onComplete, {
            categoryId:    props.categoryId,
            subCategoryId: props.subCategoryId,
            values,
        } );
    }

    const DialogComponent = props.noOp ? BasicDialog : QueryOpDialog;

    return (

        <Formik<FormValues> { ...formProps }>

            { formik => {

                const performSimilaritySearch = formik.dirty && ( formik.values.name !== existingTaxonomy.name ),
                      existingNameSearchSet   = performSimilaritySearch ? _.without( existingTaxonomyNames, existingTaxonomy.name ) : [],
                      existingNameResultMatch = performSimilaritySearch ? findTopSimilarityString( formik.values.name, existingNameSearchSet, maxScoreThreshold, {
                          useJaroWinkler: false,
                      } ) : null,
                      hasNameWarning          = !_.isNull( existingNameResultMatch ),
                      taxonomyTypeLabel       = isSubCategory ? 'Subcategory' : 'Category';

                return (

                    isLoading
                    ? (
                        <StyledContainer padding={ Size.LG }>
                            <LoadingIndicator dimmer={ false } isActive={ true } label={ 'Collecting Category Information...' }/>
                        </StyledContainer>
                    ) : (
                        <DialogComponent
                            { ...props }

                            mutation={ ProjectQueries.mutations.UPDATE_PROJECT_VERSION_DATA() }
                            graphArgs={ getGraphArgs }
                            onSubmit={ handleOnSubmit }
                            onComplete={ handleOnComplete }
                            onError={ props.onError }
                            onClose={ props.onClose }

                            operationDisabled={ !projectId || !projectVersionId || isLoading || !formik.isValid }
                            operationIsHidden={ !projectId || !projectVersionId }
                            operationInTransitMessage={ `Update ${ taxonomyTypeLabel }...` }
                            submitLabel={ `Update ${ taxonomyTypeLabel }` }
                            className={ 'no-padding' }
                            alignControls={ Align.CENTER }>

                            <Form>

                                <BasicDialogContainer
                                    title={ `Update Project ${ taxonomyTypeLabel }` }
                                    contentMaxHeight={ '60dvh' }
                                    contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                                    overflow={ true }
                                    scrolling={ true }
                                    roundedTop>

                                    { hasNameWarning && (
                                        <WarningMessage>
                                            <p className={ 'text-center' }>Corval recommends providing a more discrete name.<br/>The current name closely matches the existing { taxonomyTypeLabel.toLowerCase() }:<br/><b>"{ existingNameResultMatch.match }"</b></p>
                                        </WarningMessage>
                                    ) }

                                    { isSubCategory && (
                                        <ProjectCategoryDropdown
                                            projectId={ projectId }
                                            projectVersionId={ projectVersionId }
                                            label={ 'Parent Category' }
                                            name={ 'categoryId' }
                                            preventFirstSelection={ false }
                                            only={ getAllRelevantProjectCategories() ?? undefined }
                                            loading={ isLoading }
                                        />
                                    ) }

                                    <TextField
                                        type='text'
                                        label={ 'Name' }
                                        name={ 'name' }
                                        loading={ isLoading }/>

                                    <TextAreaField
                                        label={ 'Description' }
                                        name={ 'description' }
                                        rows={ 5 }
                                        placeholder=''
                                        loading={ isLoading }/>

                                </BasicDialogContainer>
                            </Form>

                        </DialogComponent>
                    )
                )
            } }

        </Formik>
    )
}

export {
    UpdateProjectTaxonomyDialog,
    UpdateProjectTaxonomyDialogDefaultProps,
    type UpdateProjectTaxonomyDialogProps,
};
